import { Component } from "react";

/**
 * Top-level Error Boundary.
 *
 * Without this, any render-time exception (e.g. `toast.error(<array-of-objects>)`
 * after a Pydantic 422, unexpected non-string errors, etc.) unmounts the entire
 * React tree and leaves the user on a blank black page. This boundary catches
 * those errors, logs them, and shows a recoverable UI.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6"
          data-testid="error-boundary"
        >
          <div className="max-w-md w-full bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center">
            <h1 className="text-2xl font-bold text-[#C5A059] mb-3">
              Something went wrong
            </h1>
            <p className="text-white/60 text-sm mb-6">
              We've logged the error. Please reload the page to continue.
            </p>
            {this.state.error?.message && (
              <pre className="text-white/30 text-xs bg-[#050505] border border-white/5 rounded-sm p-3 mb-6 overflow-x-auto text-left">
                {String(this.state.error.message).slice(0, 300)}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className="btn-gold px-6 py-2 rounded-sm text-sm"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
